import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { ErrorNotice, HexPattern, PoweredBy, Toggle, Wordmark } from '@hive/ui';
import { useAuth, type SignInResult } from '../auth/AuthContext';

const MESSAGES: Record<Exclude<SignInResult, 'ok'>, [string, string]> = {
  invalid: ["That username and password don't match", 'Check both and try again. Passwords are case sensitive.'],
  forbidden: ['This account is for Keeper', 'Master control is for tenant operators. Venue staff sign in to Keeper instead.'],
  unreachable: ["Master control can't reach the server", 'Check your connection, then try again.'],
};

export function LoginPage() {
  const { signIn, signedIn } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [result, setResult] = useState<SignInResult | null>(null);
  const [busy, setBusy] = useState(false);
  if (signedIn) return <Navigate to="/network" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setResult(await signIn(username, password, remember));
    setBusy(false);
  }
  const failed = result && result !== 'ok' ? MESSAGES[result] : null;

  return (
    <main className="hv-root mc-login">
      <HexPattern />
      <form className="mc-login__card" onSubmit={submit}>
        <div className="mc-row-between">
          <Wordmark />
          <span className="hv-wordmark__product">Master control</span>
        </div>
        <div className="mc-login__intro">
          <h1 className="mc-login__title">Sign in to master control</h1>
          <p className="hv-muted">Network operations for your whole estate.</p>
        </div>
        {failed && <ErrorNotice title={failed[0]}>{failed[1]}</ErrorNotice>}
        <div className="hv-field">
          <label className="hv-field__label" htmlFor="username">
            Username
          </label>
          <input id="username" className="hv-input" autoComplete="username" value={username} aria-invalid={result === 'invalid' || undefined} onChange={(e) => setUsername(e.target.value)} required />
        </div>
        <div className="hv-field">
          <label className="hv-field__label" htmlFor="password">
            Password
          </label>
          <input id="password" type="password" className="hv-input" autoComplete="current-password" value={password} aria-invalid={result === 'invalid' || undefined} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        <Toggle label="Remember this device" checked={remember} onChange={setRemember} />
        <button type="submit" className="hv-btn hv-btn--primary hv-btn--lg hv-btn--block" disabled={busy}>
          {busy ? 'Signing in…' : failed ? 'Try again' : 'Sign in'}
        </button>
      </form>
      <div className="mc-login__footer">
        <PoweredBy suffix="Master control" />
      </div>
    </main>
  );
}
